import React, { useState, useEffect } from 'react';
import { updatePartnerships } from '../../services/partnershipService';
import { removeSessionPartnershipMaybeOffline } from '../../utils/session/removeSessionPartnershipMaybeOffline';
import Button from '../common/Button';
import SectionHeader from '../common/SectionHeader';
import PlayerSearchInput from '../common/PlayerSearchInput';
import PartnershipConflictModal from './PartnershipConflictModal';
import useSessionPartnershipCreate from '../../hooks/useSessionPartnershipCreate';

export const PARTNERSHIPS_MATCHMAKING_INFO =
  'Partners usually play together. With several partnerships in a session, matchmaking may temporarily split a pair to keep games played even across the group.';

/**
 * Component for displaying and managing current partnerships on the main dashboard
 */
export default function CurrentPartnerships({
  players = [],
  partnerships = [],
  onUpdatePartnerships,
  playersById = {},
  sessionPlayerStatus = {},
  sessionId = null,
  readOnly = false,
  hideOuterHeading = false,
  onToast,
  loadSessionPartnerships,
  offlineQueue = null,
  session = null,
  getSnapshot = null,
}) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedPlayer1, setSelectedPlayer1] = useState(null);
  const [selectedPlayer2, setSelectedPlayer2] = useState(null);
  const [error, setError] = useState('');

  const { isLoading, createPartnership, conflictModalProps } = useSessionPartnershipCreate({
    sessionId,
    partnerships,
    onUpdatePartnerships,
    playersById,
    onToast,
    loadSessionPartnerships,
    offlineQueue,
    session,
    getSnapshot,
  });

  useEffect(() => {
    updatePartnerships(partnerships);
  }, [partnerships]);

  const getPlayerName = (playerId) => {
    const player = playersById[playerId] || players.find((p) => p.id === playerId);
    return player ? player.name : 'Unknown Player';
  };

  const getPlayerGender = (playerId) => {
    const player = playersById[playerId] || players.find((p) => p.id === playerId);
    return player?.gender === 'male' ? '(M)' : '(F)';
  };

  const handleRemovePartnership = async (partnershipId) => {
    const partnership = partnerships.find((p) => p.id === partnershipId);
    if (!partnership || !sessionId) {
      console.error('[PARTNERSHIP] Cannot remove partnership: missing partnership or sessionId');
      return;
    }

    try {
      const result = await removeSessionPartnershipMaybeOffline({
        sessionId,
        player1Id: partnership.player1Id,
        player2Id: partnership.player2Id,
        offlineQueue,
        session,
        snapshot: getSnapshot?.() || null,
      });

      if (result.success) {
        onUpdatePartnerships(partnerships.filter((p) => p.id !== partnershipId));
      } else {
        setError('Failed to remove partnership. Please try again.');
      }
    } catch (err) {
      console.error('[PARTNERSHIP] Error removing partnership:', err);
      setError('Failed to remove partnership. Please try again.');
    }
  };

  const handleAddPartnership = async () => {
    if (!selectedPlayer1 || !selectedPlayer2) {
      setError('Please select both players');
      return;
    }

    if (selectedPlayer1.id === selectedPlayer2.id) {
      setError('Cannot partner a player with themselves');
      return;
    }

    if (!sessionId) {
      setError('Session ID is required to create partnerships');
      return;
    }

    const existingPartnership = partnerships.find(
      (p) =>
        p.player1Id === selectedPlayer1.id ||
        p.player1Id === selectedPlayer2.id ||
        p.player2Id === selectedPlayer1.id ||
        p.player2Id === selectedPlayer2.id
    );

    if (existingPartnership) {
      setError('One or both players are already in a partnership');
      return;
    }

    setError('');
    const result = await createPartnership(selectedPlayer1.id, selectedPlayer2.id, {
      onSuccess: () => {
        setSelectedPlayer1(null);
        setSelectedPlayer2(null);
        setShowAddForm(false);
      },
    });

    if (!result.success && result.code !== 'PLAYER_ALREADY_PARTNERED' && result.error) {
      setError(result.error);
    }
  };

  const getAvailablePlayers = () => {
    const partneredPlayerIds = new Set(partnerships.flatMap((p) => [p.player1Id, p.player2Id]));
    return players.filter(
      (player) =>
        !partneredPlayerIds.has(player.id) && sessionPlayerStatus[player.id] === 'active'
    );
  };

  const availablePlayers = getAvailablePlayers();

  const transformedAvailablePlayers = availablePlayers.map((player) => {
    if (player.first_name && player.last_name) return player;
    const nameParts = (player.name || '').trim().split(' ');
    return {
      ...player,
      first_name: nameParts[0] || '',
      last_name: nameParts.slice(1).join(' ') || '',
    };
  });

  const getAvailablePlayersForPlayer1 = () => {
    if (!selectedPlayer2) return transformedAvailablePlayers;
    return transformedAvailablePlayers.filter((p) => p.id !== selectedPlayer2.id);
  };

  const getAvailablePlayersForPlayer2 = () => {
    if (!selectedPlayer1) return transformedAvailablePlayers;
    return transformedAvailablePlayers.filter((p) => p.id !== selectedPlayer1.id);
  };

  const canAddPartnership = availablePlayers.length >= 2 && !isLoading;
  const addActionButtonClass = 'text-xs px-3 py-1 w-full sm:w-auto';
  const panelClass = hideOuterHeading
    ? ''
    : 'rounded-xl border border-border-medium bg-surface p-4 shadow-lg';

  return (
    <div className={hideOuterHeading ? '' : 'my-12'}>
      {!hideOuterHeading && (
        <>
          <SectionHeader
            title="Partnerships"
            variant="info"
            infoText={PARTNERSHIPS_MATCHMAKING_INFO}
            infoAriaLabel="How partnerships work in matchmaking"
          />
          <hr className="border-t border-border-medium mb-4" />
        </>
      )}

      {!readOnly && (
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            disabled={isLoading || (!showAddForm && !canAddPartnership)}
            variant="secondary"
            size="small"
            className={addActionButtonClass}
            title={
              !showAddForm && !canAddPartnership
                ? 'Need at least 2 unpartnered playing players'
                : undefined
            }
          >
            {showAddForm ? 'Cancel' : '+ Add Partnership'}
          </Button>
        </div>
      )}

      <div className={panelClass}>
        {!readOnly && showAddForm && canAddPartnership && (
          <div className="mb-4 p-4 bg-surface-subtle rounded-lg border border-border">
            <h3 className="text-sm font-medium mb-3 text-text-primary">Create New Partnership</h3>
            <div className="flex flex-wrap gap-3 mb-3">
              <div className="flex-1">
                <label className="block text-xs text-text-muted mb-1">Player 1</label>
                <PlayerSearchInput
                  placeholder="Search for Player 1..."
                  availablePlayers={getAvailablePlayersForPlayer1()}
                  selectedPlayer={selectedPlayer1}
                  onSelectPlayer={(player) => setSelectedPlayer1(player)}
                  onRemovePlayer={() => setSelectedPlayer1(null)}
                  disabled={isLoading}
                  className="w-full"
                />
              </div>

              <div className="flex-1">
                <label className="block text-xs text-text-muted mb-1">Player 2</label>
                <PlayerSearchInput
                  placeholder="Search for Player 2..."
                  availablePlayers={getAvailablePlayersForPlayer2()}
                  selectedPlayer={selectedPlayer2}
                  onSelectPlayer={(player) => setSelectedPlayer2(player)}
                  onRemovePlayer={() => setSelectedPlayer2(null)}
                  disabled={isLoading}
                  className="w-full"
                />
              </div>
            </div>

            {error && <div className="text-danger-dark text-xs mb-3">{error}</div>}

            <div className="flex justify-end">
              <Button onClick={handleAddPartnership} disabled={isLoading} variant="primary" size="small">
                {isLoading ? 'Creating...' : 'Create Partnership'}
              </Button>
            </div>
          </div>
        )}

        {partnerships.length > 0 ? (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {partnerships.map((partnership) => (
              <li
                key={partnership.id}
                className="flex items-center justify-between bg-surface-subtle p-3 rounded-lg border border-border"
              >
                <div className="flex items-center">
                  <span className="font-medium text-text-primary">
                    {getPlayerName(partnership.player1Id)}{' '}
                    <span className="text-xs text-text-muted">{getPlayerGender(partnership.player1Id)}</span>
                  </span>
                  <span className="mx-2 text-text-muted">+</span>
                  <span className="font-medium text-text-primary">
                    {getPlayerName(partnership.player2Id)}{' '}
                    <span className="text-xs text-text-muted">{getPlayerGender(partnership.player2Id)}</span>
                  </span>
                </div>
                {!readOnly && (
                  <button
                    onClick={() => handleRemovePartnership(partnership.id)}
                    disabled={isLoading}
                    className="text-danger-dark hover:text-danger disabled:opacity-50 bg-surface rounded-full w-6 h-6 flex items-center justify-center shadow-sm transition-colors border border-border"
                    aria-label="Remove partnership"
                  >
                    &times;
                  </button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-text-muted">No partnerships yet.</p>
        )}
      </div>

      <PartnershipConflictModal {...conflictModalProps} />
    </div>
  );
}
